import type { CSSProperties } from "react";
import { Check, ExternalLink, Palette } from "lucide-react";
import {
  SITE_THEME_PRESETS,
  resolveSiteTheme,
  type SiteTheme,
  type SiteThemePreset,
} from "@ease/page-builder";

type ThemeCanvasStyle = CSSProperties & Record<string, string | number | undefined>;

function withFallback(value: string | undefined, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}

function buildFontStack(family: string | undefined): string {
  return family ? `'${family}', var(--font-sans), system-ui, sans-serif` : "var(--font-sans), system-ui, sans-serif";
}

export function buildEditorCanvasThemeStyle(theme: SiteTheme | null | undefined): ThemeCanvasStyle {
  const resolved = resolveSiteTheme(theme);
  const brand = withFallback(resolved.colors?.brand, "#1040CC");
  const brandDark = withFallback(resolved.colors?.brandDark, brand);
  const secondary = withFallback(resolved.colors?.secondary, brandDark);
  const accent = withFallback(resolved.colors?.accent, "#F05E0A");
  const background = withFallback(resolved.colors?.background, "#FFFFFF");
  const surface = withFallback(resolved.colors?.surface, "#F8FAFC");
  const muted = withFallback(resolved.colors?.muted, "#E2E8F0");
  const text = withFallback(resolved.colors?.text, "#111827");

  return {
    "--color-primary": brand,
    "--color-primary-foreground": "#ffffff",
    "--color-primary-hover": brandDark,
    "--color-primary-light": surface,
    "--color-primary-border": muted,
    "--color-secondary": secondary,
    "--color-secondary-foreground": "#ffffff",
    "--color-accent": accent,
    "--color-accent-foreground": "#ffffff",
    "--color-accent-action": accent,
    "--color-accent-action-foreground": "#ffffff",
    "--color-accent-action-light": surface,
    "--color-background": background,
    "--color-surface": surface,
    "--color-foreground": text,
    "--color-muted": muted,
    "--color-muted-foreground": secondary,
    "--color-border": muted,
    "--color-input": muted,
    "--color-ring": brand,
    "--color-card": surface,
    "--color-card-foreground": text,
    "--gradient-energy": `linear-gradient(135deg, ${brand} 0%, ${accent} 100%)`,
    "--editor-theme-heading-font": buildFontStack(resolved.fonts?.heading?.family),
    "--editor-theme-body-font": buildFontStack(resolved.fonts?.body?.family),
    "--radius": withFallback(resolved.borderRadius, "0.5rem"),
    fontFamily: buildFontStack(resolved.fonts?.body?.family),
    backgroundColor: background,
    color: text,
  };
}

export function buildEditorThemeScopeCss(scopeClass: string): string {
  return `.${scopeClass}{font-family:var(--editor-theme-body-font,var(--font-sans),system-ui,sans-serif);}
.${scopeClass} h1,.${scopeClass} h2,.${scopeClass} h3,.${scopeClass} h4,.${scopeClass} h5,.${scopeClass} h6{font-family:var(--editor-theme-heading-font,var(--editor-theme-body-font,var(--font-sans),system-ui,sans-serif));}`;
}

interface EditorThemePresetPickerProps {
  readonly activePresetId: string | null;
  readonly isSaving: boolean;
  readonly isDisabled: boolean;
  readonly onSelectPreset: (preset: SiteThemePreset) => void;
  readonly onOpenSettings: () => void;
}

export function EditorThemePresetPicker({
  activePresetId,
  isSaving,
  isDisabled,
  onSelectPreset,
  onOpenSettings,
}: EditorThemePresetPickerProps) {
  return (
    <div className="absolute right-0 top-full z-20 mt-2 w-[360px] rounded-2xl border border-border bg-card p-3 shadow-xl">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Theme Presets</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Select a site-wide preset. The editor canvas previews it immediately and the site theme persists on save.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenSettings}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition hover:bg-accent"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Full theme
        </button>
      </div>

      <div className="grid gap-2">
        {SITE_THEME_PRESETS.map((preset) => {
          const isActive = activePresetId === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              disabled={isDisabled || isSaving}
              onClick={() => onSelectPreset(preset)}
              className={`rounded-2xl border p-3 text-left transition ${
                isActive
                  ? "border-primary bg-primary/5"
                  : "border-border bg-background hover:border-foreground/20 hover:bg-accent"
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">{preset.name}</p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{preset.description}</p>
                </div>
                {isActive ? <Check className="mt-0.5 h-4 w-4 text-primary" /> : null}
              </div>
              <div className="mt-3 flex gap-2">
                {[
                  preset.theme.colors?.brand,
                  preset.theme.colors?.secondary,
                  preset.theme.colors?.accent,
                  preset.theme.colors?.surface,
                ].map((color) => (
                  <span
                    key={`${preset.id}-${color}`}
                    className="h-7 w-7 rounded-full border border-black/10"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>{preset.theme.fonts?.heading?.family}</span>
                <span>{preset.theme.buttonStyle} / {preset.theme.cardStyle}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function EditorThemeButtonLabel(activePresetId: string | null): string {
  const label = SITE_THEME_PRESETS.find((preset) => preset.id === activePresetId)?.name;
  return label ? `Theme: ${label}` : "Theme";
}

export const EditorThemeButtonIcon = Palette;
